/**
 * Mixpanel Event Tracking Utilities
 *
 * This module provides functions for tracking custom events in Mixpanel.
 * All functions safely check if Mixpanel is loaded before tracking.
 */
import { subscriptionPlanDisplayName } from "shared/utils/tiers"

/**
 * Safely track a Mixpanel event
 * @param {string} eventName - The name of the event
 * @param {object} properties - Event properties
 */
type AppletOwnerType = "user" | "service"
type AppletPublishedType = "published" | "diy"
type AppletConfigType = "static" | "dynamic"
type AuthType = "oauth" | "pin" | "token" | "modular" | "auto"
type ConnectionContext = "trigger" | "action" | "query" | "manual"
type PlanInterval = "month" | "year"
type SubscriptionPricingModel = "monthly" | "annual"
type SubscriptionName = "pro" | "pro_plus" | "free"
type ServiceConnectionEntryPoint = "diy_composer" | "published_applet_enable" | "service_settings"
type AuthMethod = "email_password" | "facebook" | "google" | "apple"
type SignupProvider = "email" | "facebook" | "google" | "apple"
type SubscriptionStatus = string
export type { PaymentMethodType } from "app/utils/payment_method_type"
export { normalizePaymentMethodType } from "app/utils/payment_method_type"

const REPLAY_MAX_CAPTURE_MS = 120000
let replayIsActive = false
let replayStopTimerId: number | undefined

const isFunnelStartEvent = (eventName: string) =>
  eventName.endsWith(" Started") || eventName === "Subscription Paywall Shown"

const REPLAY_END_EXCEPTIONS = new Set(["Conversational UI Turn Completed", "Applet Field Validation Completed"])

const isFunnelEndEvent = (eventName: string) =>
  !REPLAY_END_EXCEPTIONS.has(eventName) &&
  (eventName.endsWith(" Completed") || eventName.endsWith(" Failed") || eventName.endsWith(" Cancelled"))

const isUserTimeZoneOutsideUSA = (window as any)?.App?.isUserTimeZoneInUSA != true

const isMixpanelReplayBlocked = (): boolean => {
  try {
    return (window as any)?.App?.mixpanelSessionReplayBlocked === true
  } catch {
    return false
  }
}

const startReplayIfNeeded = () => {
  if (replayIsActive) return
  if (isMixpanelReplayBlocked()) return
  if (isUserTimeZoneOutsideUSA) return
  const mixpanelAny = (window as any)?.mixpanel as any
  if (typeof mixpanelAny?.start_session_recording !== "function") return

  replayIsActive = true
  mixpanelAny.start_session_recording()

  replayStopTimerId = window.setTimeout(() => {
    const mp = (window as any)?.mixpanel as any
    if (typeof mp?.stop_session_recording === "function") mp.stop_session_recording()
    replayIsActive = false
  }, REPLAY_MAX_CAPTURE_MS)
}

const stopReplayIfNeeded = () => {
  if (!replayIsActive) return
  const mixpanelAny = (window as any)?.mixpanel as any
  if (typeof mixpanelAny?.stop_session_recording === "function") mixpanelAny.stop_session_recording()

  replayIsActive = false
  if (typeof replayStopTimerId === "number") window.clearTimeout(replayStopTimerId)
  replayStopTimerId = undefined
}

// Event volume protection:
// - Dedupe "Started" events that can fire multiple times due to retries / double-submits.
const DEDUPE_WINDOW_MS = 20_000
const startedDedupeMemory = new Map<string, number>()

const normalizeEntrypoint = (entrypoint?: string | null): string => {
  if (typeof entrypoint !== "string") return "unknown"
  const normalized = entrypoint.trim()
  return normalized.length > 0 ? normalized : "unknown"
}

const detectBrowserType = (): string => {
  const appBrowserType = (window as any)?.App?.browserType || (window as any)?.App?.browser_type
  if (typeof appBrowserType === "string" && appBrowserType.trim().length > 0) {
    return appBrowserType.trim().toLowerCase()
  }

  const userAgent = window.navigator?.userAgent || ""
  if (userAgent.includes("Edg/")) return "edge"
  if (userAgent.includes("Firefox/")) return "firefox"
  if (userAgent.includes("Chrome/")) return "chrome"
  if (userAgent.includes("Safari/") && !userAgent.includes("Chrome/")) return "safari"

  return "unknown"
}

const getStartedDedupeKey = (eventName: string, properties: Record<string, unknown>) => {
  if (!eventName.endsWith(" Started")) return null

  const entrypoint = properties.entrypoint ? String(properties.entrypoint) : ""

  if (eventName === "Sign Up Started" || eventName === "Login Started") {
    const authMethod = properties.auth_method ? String(properties.auth_method) : ""
    const signupProvider = properties.signup_provider ? String(properties.signup_provider) : ""
    return `${authMethod}|${signupProvider}|${entrypoint}`
  }

  if (eventName === "Service Connection Started") {
    const serviceModuleName = properties.service_module_name ? String(properties.service_module_name) : ""
    const connectionContext = properties.connection_context ? String(properties.connection_context) : ""
    const isNewConnection = typeof properties.is_new_connection === "boolean" ? String(properties.is_new_connection) : ""
    const authType = properties.auth_type ? String(properties.auth_type) : ""
    return `${serviceModuleName}|${connectionContext}|${isNewConnection}|${authType}|${entrypoint}`
  }

  if (eventName === "Applet Enable Started") {
    const appletSlug = properties.applet_slug ? String(properties.applet_slug) : ""
    const configType = properties.applet_config_type ? String(properties.applet_config_type) : ""
    return `${appletSlug}|${configType}|${entrypoint}`
  }

  if (eventName === "Applet Enable Configuration Started") {
    const appletSlug = properties.applet_slug ? String(properties.applet_slug) : ""
    const configType = properties.config_type ? String(properties.config_type) : ""
    return `${appletSlug}|${configType}|${entrypoint}`
  }

  if (eventName === "Applet Creation Started") {
    const creationMode = properties.creation_mode ? String(properties.creation_mode) : ""
    const creationAttemptId = properties.creation_attempt_id ? String(properties.creation_attempt_id) : ""
    return `${creationMode}|${creationAttemptId}|${entrypoint}`
  }

  if (eventName === "Subscription Payment Started") {
    const subscriptionName = properties.subscription_name ? String(properties.subscription_name) : ""
    const pricingModel = properties.subscription_pricing_model ? String(properties.subscription_pricing_model) : ""
    const paymentMethodType = properties.payment_method_type ? String(properties.payment_method_type) : ""
    const eligibleForTrial = typeof properties.eligible_for_trial === "boolean" ? String(properties.eligible_for_trial) : ""
    return `${subscriptionName}|${pricingModel}|${paymentMethodType}|${eligibleForTrial}|${entrypoint}`
  }

  if (eventName === "Payment Method Update Started") {
    const paymentMethodType = properties.payment_method_type ? String(properties.payment_method_type) : ""
    const requiresAction = typeof properties.requires_action === "boolean" ? String(properties.requires_action) : ""
    return `${paymentMethodType}|${requiresAction}|${entrypoint}`
  }

  // Only dedupe started events we know could be retried/double-submitted.
  return null
}

const shouldDedupeStartedEvent = (eventName: string, properties: Record<string, unknown>) => {
  const key = getStartedDedupeKey(eventName, properties)
  if (!key) return false

  const dedupeKey = `mxp_started_dedupe:${eventName}:${key}`
  const now = Date.now()

  const lastInMemory = startedDedupeMemory.get(dedupeKey)
  if (typeof lastInMemory === "number" && now - lastInMemory < DEDUPE_WINDOW_MS) return true

  // Also try sessionStorage to survive navigation / bfcache.
  try {
    const lastStr = window.sessionStorage.getItem(dedupeKey)
    const last = lastStr ? Number(lastStr) : undefined
    if (typeof last === "number" && now - last < DEDUPE_WINDOW_MS) return true
    startedDedupeMemory.set(dedupeKey, now)
    window.sessionStorage.setItem(dedupeKey, String(now))
  } catch {
    startedDedupeMemory.set(dedupeKey, now)
  }

  return false
}

type TrackOptions = { transport: "sendBeacon" }

const trackEvent = (eventName: string, properties: Record<string, unknown> = {}, options?: TrackOptions) => {
  if (isFunnelEndEvent(eventName)) stopReplayIfNeeded()
  if (isFunnelStartEvent(eventName)) startReplayIfNeeded()

  // Dedupe started events (can double-fire on retries / double-submits).
  if (eventName.endsWith(" Started") && shouldDedupeStartedEvent(eventName, properties)) return

  const user = (window as any)?.App?.user
  if (user?.id) {
    const user_traits_snapshot: Record<string, unknown> = {
      plan: (user.tier && toSubscriptionName(user.tier)) || "free",
      eligible_for_trial: user.eligible_for_trial,
    }
    if (user.payment_interval) {
      user_traits_snapshot.payment_interval = toPricingModel(user.payment_interval)
    }
    if (user.payment_platform) {
      user_traits_snapshot.payment_platform = user.payment_platform
    }
    Object.assign(properties, { user_traits_snapshot })
  }
  if (!properties.browser_type) {
    Object.assign(properties, { browser_type: detectBrowserType() })
  }
  // Copy attribution: which AI-generated content versions rendered on this
  // page ({content_type: version}, e.g. {service_page_intro: "…g3"}). Set
  // server-side via window.App; see ifttt_front_end doc/ai-content.md.
  const aiContentVersions = (window as any)?.App?.aiContentVersions
  if (aiContentVersions && Object.keys(aiContentVersions).length > 0) {
    Object.assign(properties, { ai_content_versions: aiContentVersions })
  }
  ;(window as any)?.mixpanel?.track?.(eventName, properties, options)
}

// ============================================================================
// APPLET LIFECYCLE EVENTS
// ============================================================================

interface TrackAppletEnableStartedPayload {
  appletId: string
  appletOwnerType: AppletOwnerType
  appletPublishedType: AppletPublishedType
  appletConfigType: AppletConfigType
  appletAuthor: string
  triggerService: string
  actionServices: string[]
  appletTags: string[]
}
export const trackAppletEnableStarted = ({
  appletId,
  appletOwnerType,
  appletPublishedType,
  appletConfigType,
  appletAuthor,
  triggerService,
  actionServices,
  appletTags,
}: TrackAppletEnableStartedPayload) => {
  trackEvent("Applet Enable Started", {
    applet_slug: appletId,
    applet_owner_type: appletOwnerType, // "user" | "service"
    applet_published_type: appletPublishedType, // "published" | "diy"
    applet_config_type: appletConfigType, // "static" | "dynamic"
    applet_author: appletAuthor,
    trigger_service: triggerService,
    action_services: actionServices,
    applet_tags: appletTags,
  })
}

// Applet JSON from Rails has no trigger key; the trigger permission id is "/triggers/<service>.<trigger>".
export const triggerModuleNameFromPermissions = (
  permissions?: Array<{ id?: string; type?: string }> | null,
): string | null => {
  const id = permissions?.find(p => p.type === "trigger")?.id
  return id ? id.replace(/^\/triggers\//, "") : null
}

interface TrackAppletEnableCompletedPayload extends TrackAppletEnableStartedPayload {
  triggerModuleName?: string | null
  appletStatus: string
  appletTier: string
  queryCount?: number
  actionCount?: number
  hasFilterCode?: boolean
  hasDelay?: boolean
}
export const trackAppletEnableCompleted = ({
  appletId,
  appletOwnerType,
  appletPublishedType,
  appletConfigType,
  appletAuthor,
  appletStatus,
  appletTier,
  triggerService,
  triggerModuleName,
  actionServices,
  appletTags,
  queryCount,
  actionCount,
  hasFilterCode,
  hasDelay,
}: TrackAppletEnableCompletedPayload) => {
  trackEvent("Applet Enable Completed", {
    applet_slug: appletId,
    applet_owner_type: appletOwnerType,
    applet_published_type: appletPublishedType,
    applet_config_type: appletConfigType,
    applet_author: appletAuthor,
    applet_status: appletStatus,
    applet_tier: appletTier,
    trigger_service: triggerService,
    trigger_module_name: triggerModuleName || null,
    action_services: actionServices,
    applet_tags: appletTags,
    query_count: queryCount || 0,
    action_count: actionCount || 0,
    has_filter_code: hasFilterCode || false,
    has_delay: hasDelay || false,
  })
}

interface TrackAppletEnableConfigurationStartedPayload {
  appletId: string
  appletOwnerType: AppletOwnerType
  configType: "initial" | "edit"
  hasExistingConfig: boolean
  appletTags: string[]
}
export const trackAppletEnableConfigurationStarted = ({
  appletId,
  appletOwnerType,
  configType,
  hasExistingConfig,
  appletTags,
}: TrackAppletEnableConfigurationStartedPayload) => {
  trackEvent("Applet Enable Configuration Started", {
    applet_slug: appletId,
    applet_owner_type: appletOwnerType,
    config_type: configType,
    has_existing_config: hasExistingConfig,
    applet_tags: appletTags,
  })
}

interface TrackAppletEnableConfigurationCompletedPayload extends TrackAppletEnableConfigurationStartedPayload {
  fieldCount?: number
  timeToComplete?: number
}
export const trackAppletEnableConfigurationCompleted = ({
  appletId,
  appletOwnerType,
  configType,
  hasExistingConfig,
  appletTags,
  fieldCount,
  timeToComplete,
}: TrackAppletEnableConfigurationCompletedPayload) => {
  trackEvent("Applet Enable Configuration Completed", {
    applet_slug: appletId,
    applet_owner_type: appletOwnerType,
    config_type: configType,
    has_existing_config: hasExistingConfig,
    applet_tags: appletTags,
    field_count: fieldCount || 0,
    time_to_complete: timeToComplete,
  })
}

// ============================================================================
// SERVICE CONNECTION EVENTS
// ============================================================================

interface TrackServiceConnectionStartedPayload {
  serviceName: string
  serviceModuleName: string
  authType: AuthType
  connectionContext: ConnectionContext
  isNewConnection: boolean
  inDiyFlow?: boolean
  entrypoint?: ServiceConnectionEntryPoint
}
export const trackServiceConnectionStarted = ({
  serviceName,
  serviceModuleName,
  authType,
  connectionContext,
  isNewConnection,
  inDiyFlow,
  entrypoint,
}: TrackServiceConnectionStartedPayload) => {
  trackEvent("Service Connection Started", {
    service_name: serviceName,
    service_module_name: serviceModuleName,
    auth_type: authType, // "oauth" | "pin" | "token" | "modular"
    connection_context: connectionContext, // "trigger" | "action" | "query" | "manual"
    is_new_connection: isNewConnection,
    in_diy_flow: inDiyFlow || false,
    entrypoint: entrypoint || null,
  })
}

interface TrackServiceConnectionCompletedPayload extends TrackServiceConnectionStartedPayload {
  liveChannelId?: string
  timeToConnect?: number
}
export const trackServiceConnectionCompleted = ({
  serviceName,
  serviceModuleName,
  authType,
  connectionContext,
  isNewConnection,
  inDiyFlow,
  entrypoint,
  liveChannelId,
  timeToConnect,
}: TrackServiceConnectionCompletedPayload) => {
  trackEvent("Service Connection Completed", {
    service_name: serviceName,
    service_module_name: serviceModuleName,
    auth_type: authType,
    connection_context: connectionContext,
    is_new_connection: isNewConnection,
    in_diy_flow: inDiyFlow || false,
    entrypoint: entrypoint || null,
    live_channel_id: liveChannelId,
    time_to_connect: timeToConnect,
  })
}

// ============================================================================
// SUBSCRIPTION EVENTS
// ============================================================================

// Helper to convert planId to subscription name
const toSubscriptionName = (planId: string): SubscriptionName => {
  switch (planId) {
    case "intermediate_pro":
      return "pro"
    case "pro":
      return "pro_plus"
    default:
      return planId as SubscriptionName
  }
}

// Helper to convert plan interval to pricing model
const toPricingModel = (interval: PlanInterval): SubscriptionPricingModel => {
  switch (interval) {
    case "month":
      return "monthly"
    case "year":
      return "annual"
  }
}

interface TrackSubscriptionPaymentStartedPayload {
  planId: string
  planInterval: PlanInterval
  currency: string
  eligibleForTrial?: boolean
  promoCode?: string | null
  paymentMethodType: PaymentMethodType
  entrypoint?: string
}

interface TrackSubscriptionPaymentMethodSelectedPayload {
  planId: string
  planInterval: PlanInterval
  currency: string
  eligibleForTrial?: boolean
  promoCode?: string | null
  paymentMethodType: PaymentMethodType
  isBillingUpdateFlow?: boolean
  entrypoint?: string
}
export const trackSubscriptionPaymentMethodSelected = ({
  planId,
  planInterval,
  currency,
  eligibleForTrial,
  promoCode,
  paymentMethodType,
  isBillingUpdateFlow,
  entrypoint,
}: TrackSubscriptionPaymentMethodSelectedPayload) => {
  const subscriptionName = toSubscriptionName(planId)
  trackEvent("Subscription Payment Method Selected", {
    subscription_name: subscriptionName,
    subscription_pricing_model: toPricingModel(planInterval),
    currency,
    eligible_for_trial: eligibleForTrial || false,
    promo_code: promoCode || null,
    payment_method_type: paymentMethodType,
    target_tier: subscriptionPlanDisplayName(subscriptionName),
    is_billing_update_flow: !!isBillingUpdateFlow,
    entrypoint: normalizeEntrypoint(entrypoint),
  })
}
export const trackSubscriptionPaymentStarted = ({
  planId,
  planInterval,
  currency,
  eligibleForTrial,
  promoCode,
  paymentMethodType,
  entrypoint,
}: TrackSubscriptionPaymentStartedPayload) => {
  const subscriptionName = toSubscriptionName(planId)
  trackEvent("Subscription Payment Started", {
    subscription_name: subscriptionName,
    subscription_pricing_model: toPricingModel(planInterval),
    currency,
    eligible_for_trial: eligibleForTrial || false,
    promo_code: promoCode || null,
    payment_method_type: paymentMethodType,
    target_tier: subscriptionPlanDisplayName(subscriptionName),
    entrypoint: normalizeEntrypoint(entrypoint),
  })
}

interface TrackSubscriptionCurrencyChangedPayload {
  planId: string
  planInterval: PlanInterval
  fromCurrency: string
  toCurrency: string
  eligibleForTrial?: boolean
  entrypoint?: string
}
export const trackSubscriptionCurrencyChanged = ({
  planId,
  planInterval,
  fromCurrency,
  toCurrency,
  eligibleForTrial,
  entrypoint,
}: TrackSubscriptionCurrencyChangedPayload) => {
  const subscriptionName = toSubscriptionName(planId)
  trackEvent("Subscription Currency Changed", {
    subscription_name: subscriptionName,
    subscription_pricing_model: toPricingModel(planInterval),
    from_currency: fromCurrency,
    to_currency: toCurrency,
    eligible_for_trial: eligibleForTrial || false,
    target_tier: subscriptionPlanDisplayName(subscriptionName),
    entrypoint: normalizeEntrypoint(entrypoint),
  })
}

interface TrackSubscriptionPaymentCompletedPayload {
  planId: string
  planInterval: PlanInterval
  currency: string
  promoCode?: string | null
  eligibleForTrial?: boolean
  paymentMethodType: PaymentMethodType
  subscriptionStatus?: SubscriptionStatus
  isTrialing?: boolean
  entrypoint?: string
}
export const trackSubscriptionPaymentCompleted = ({
  planId,
  planInterval,
  currency,
  promoCode,
  eligibleForTrial,
  paymentMethodType,
  subscriptionStatus,
  isTrialing,
  entrypoint,
}: TrackSubscriptionPaymentCompletedPayload) => {
  const subscriptionName = toSubscriptionName(planId)
  const computedIsTrialing = typeof isTrialing === "boolean" ? isTrialing : subscriptionStatus === "trialing"
  trackEvent("Subscription Payment Completed", {
    subscription_name: subscriptionName,
    subscription_pricing_model: toPricingModel(planInterval),
    currency,
    promo_code: promoCode || null,
    eligible_for_trial: eligibleForTrial || false,
    payment_method_type: paymentMethodType,
    target_tier: subscriptionPlanDisplayName(subscriptionName),
    subscription_status: subscriptionStatus || null,
    is_trialing: computedIsTrialing || false,
    entrypoint: normalizeEntrypoint(entrypoint),
  })
}

// ============================================================================
// PAYWALL EVENTS
// ============================================================================

type PaywallLocation =
  | "Applet Config"
  | "Applet Details"
  | "AI Applet Creator"
  | "Classic Applet Creator"
  | "Home"
  | "My Applets"
  | "Plans"
  | "Service"
  | "Connect"
  | "Settings"

type PaywallReason =
  | "Applet Features"
  | "Applet Quota"
  | "Rate Limit"
  | "Onboarding"
  | "Banner"
  | "Visit"

interface TrackSubscriptionPaywallShownPayload {
  location: PaywallLocation
  reason: PaywallReason
  targetTier?: string
  entrypoint?: string
}
export const trackSubscriptionPaywallShown = ({
  location,
  reason,
  targetTier,
  entrypoint,
}: TrackSubscriptionPaywallShownPayload) => {
  trackEvent("Subscription Paywall Shown", {
    location,
    reason,
    target_tier: targetTier,
    entrypoint: entrypoint || null,
  })
}

// ============================================================================
// AUTH EVENTS
// ============================================================================

interface TrackSignUpStartedPayload {
  authMethod: AuthMethod
  signupProvider: SignupProvider
  entrypoint?: string
}
export const trackSignUpStarted = ({ authMethod, signupProvider, entrypoint }: TrackSignUpStartedPayload) => {
  trackEvent("Sign Up Started", {
    auth_method: authMethod,
    signup_provider: signupProvider,
    entrypoint: entrypoint || null,
  })
}

interface TrackSignUpCompletedPayload extends TrackSignUpStartedPayload {}
export const trackSignUpCompleted = ({ authMethod, signupProvider, entrypoint }: TrackSignUpCompletedPayload) => {
  trackEvent("Sign Up Completed", {
    auth_method: authMethod,
    signup_provider: signupProvider,
    entrypoint: entrypoint || null,
  })
}

interface TrackLoginStartedPayload {
  authMethod: AuthMethod
  signupProvider: SignupProvider
  entrypoint?: string
}
export const trackLoginStarted = ({ authMethod, signupProvider, entrypoint }: TrackLoginStartedPayload) => {
  trackEvent("Login Started", {
    auth_method: authMethod,
    signup_provider: signupProvider,
    entrypoint: entrypoint || null,
  })
}

interface TrackLoginCompletedPayload extends TrackLoginStartedPayload {}
export const trackLoginCompleted = ({ authMethod, signupProvider, entrypoint }: TrackLoginCompletedPayload) => {
  trackEvent("Login Completed", {
    auth_method: authMethod,
    signup_provider: signupProvider,
    entrypoint: entrypoint || null,
  })
}

interface TrackAuthFailedPayload {
  eventType: "Sign Up" | "Login"
  authMethod: AuthMethod
  signupProvider: SignupProvider
  errorType?: string
  entrypoint?: string
}
export const trackAuthFailed = ({ eventType, authMethod, signupProvider, errorType, entrypoint }: TrackAuthFailedPayload) => {
  trackEvent(`${eventType} Failed`, {
    auth_method: authMethod,
    signup_provider: signupProvider,
    error_type: errorType || null,
    entrypoint: entrypoint || null,
  })
}

// ============================================================================
// SERVICE CONNECTION FAILURES
// ============================================================================

interface TrackServiceConnectionFailedPayload {
  serviceName: string
  serviceModuleName: string
  authType: AuthType
  connectionContext: ConnectionContext
  isNewConnection: boolean
  entrypoint?: ServiceConnectionEntryPoint
  errorType?: string
}
export const trackServiceConnectionFailed = ({
  serviceName,
  serviceModuleName,
  authType,
  connectionContext,
  isNewConnection,
  entrypoint,
  errorType,
}: TrackServiceConnectionFailedPayload) => {
  trackEvent("Service Connection Failed", {
    service_name: serviceName,
    service_module_name: serviceModuleName,
    auth_type: authType,
    connection_context: connectionContext,
    is_new_connection: isNewConnection,
    entrypoint: entrypoint || null,
    error_type: errorType || null,
  })
}

// ============================================================================
// APPLET CREATION (DIY composer)
// ============================================================================

export type AppletCreationMode = "classic" | "ai" | "template" | "try_it" | "build_automation"

interface TrackAppletCreationStartedPayload {
  creationMode: AppletCreationMode
  creationAttemptId?: string
  entrypoint?: string
}
const appletCreationBaseProperties = ({
  creationMode,
  creationAttemptId,
  entrypoint,
}: TrackAppletCreationStartedPayload) => ({
  creation_mode: creationMode,
  creation_attempt_id: creationAttemptId || null,
  entrypoint: entrypoint || null,
})

export const trackAppletCreationStarted = (payload: TrackAppletCreationStartedPayload) => {
  trackEvent("Applet Creation Started", appletCreationBaseProperties(payload))
}

export const trackAppletCreatePreviewShown = (payload: TrackAppletCreationStartedPayload) => {
  trackEvent("Applet Create Preview Shown", appletCreationBaseProperties(payload))
}

interface TrackAppletCreationCompletedPayload extends TrackAppletCreationStartedPayload {
  appletId?: string
  triggerModuleName?: string | null
  hasFilterCode?: boolean
  setDatastoreInFilterCode?: boolean
  usedDatastoreValueIngredient?: boolean
  datastoreKeysCount?: number
}
export const trackAppletCreationCompleted = ({
  appletId,
  triggerModuleName,
  hasFilterCode,
  setDatastoreInFilterCode,
  usedDatastoreValueIngredient,
  datastoreKeysCount,
  ...base
}: TrackAppletCreationCompletedPayload) => {
  trackEvent("Applet Creation Completed", {
    ...appletCreationBaseProperties(base),
    applet_slug: appletId || null,
    trigger_module_name: triggerModuleName || null,
    has_filter_code: hasFilterCode ?? false,
    set_datastore_in_filter_code: setDatastoreInFilterCode ?? false,
    used_datastore_value_ingredient: usedDatastoreValueIngredient ?? false,
    datastore_keys_count: datastoreKeysCount ?? 0,
  })
}

interface TrackAppletCreationFailedPayload extends TrackAppletCreationStartedPayload {
  errorType?: string
  hasFilterCode?: boolean
  setDatastoreInFilterCode?: boolean
  usedDatastoreValueIngredient?: boolean
  datastoreKeysCount?: number
}
export const trackAppletCreationFailed = ({
  errorType,
  hasFilterCode,
  setDatastoreInFilterCode,
  usedDatastoreValueIngredient,
  datastoreKeysCount,
  ...base
}: TrackAppletCreationFailedPayload) => {
  trackEvent("Applet Creation Failed", {
    ...appletCreationBaseProperties(base),
    error_type: errorType || null,
    has_filter_code: hasFilterCode ?? false,
    set_datastore_in_filter_code: setDatastoreInFilterCode ?? false,
    used_datastore_value_ingredient: usedDatastoreValueIngredient ?? false,
    datastore_keys_count: datastoreKeysCount ?? 0,
  })
}

interface TrackAppletFieldValidationCompletedPayload {
  stepType: "trigger" | "query" | "action"
  stepModuleName: string
  isValid: boolean
}
// Field values and error text stay out: they can hold private URLs and tokens.
export const trackAppletFieldValidationCompleted = ({
  stepType,
  stepModuleName,
  isValid,
}: TrackAppletFieldValidationCompletedPayload) => {
  trackEvent("Applet Field Validation Completed", {
    step_type: stepType,
    step_module_name: stepModuleName,
    is_valid: isValid,
  })
}

// ============================================================================
// TRIAL / UPGRADE EVENTS
// ============================================================================

interface TrackSubscriptionPaymentCancelledPayload {
  planId: string
  planInterval: PlanInterval
  currency: string
  paymentMethodType: PaymentMethodType
  subscriptionStatus?: SubscriptionStatus
  isTrialing?: boolean
  promoCode?: string | null
  eligibleForTrial?: boolean
  entrypoint?: string
}
export const trackSubscriptionPaymentCancelled = ({
  planId,
  planInterval,
  currency,
  paymentMethodType,
  subscriptionStatus,
  isTrialing,
  promoCode,
  eligibleForTrial,
  entrypoint,
}: TrackSubscriptionPaymentCancelledPayload) => {
  const subscriptionName = toSubscriptionName(planId)
  const computedIsTrialing = typeof isTrialing === "boolean" ? isTrialing : subscriptionStatus === "trialing"
  trackEvent("Subscription Payment Cancelled", {
    subscription_name: subscriptionName,
    subscription_pricing_model: toPricingModel(planInterval),
    currency,
    promo_code: promoCode || null,
    eligible_for_trial: eligibleForTrial || false,
    payment_method_type: paymentMethodType,
    target_tier: subscriptionPlanDisplayName(subscriptionName),
    subscription_status: subscriptionStatus || null,
    is_trialing: computedIsTrialing || false,
    entrypoint: normalizeEntrypoint(entrypoint),
  })
}

interface TrackSubscriptionPaymentFailedPayload extends TrackSubscriptionPaymentCancelledPayload {
  errorType?: string
}
export const trackSubscriptionPaymentFailed = ({
  errorType,
  ...rest
}: TrackSubscriptionPaymentFailedPayload) => {
  const { planId, planInterval, currency, paymentMethodType, subscriptionStatus, isTrialing, promoCode, eligibleForTrial, entrypoint } = rest
  const subscriptionName = toSubscriptionName(planId)
  const computedIsTrialing = typeof isTrialing === "boolean" ? isTrialing : subscriptionStatus === "trialing"
  trackEvent("Subscription Payment Failed", {
    subscription_name: subscriptionName,
    subscription_pricing_model: toPricingModel(planInterval),
    currency,
    promo_code: promoCode || null,
    eligible_for_trial: eligibleForTrial || false,
    payment_method_type: paymentMethodType,
    target_tier: subscriptionPlanDisplayName(subscriptionName),
    subscription_status: subscriptionStatus || null,
    is_trialing: computedIsTrialing || false,
    error_type: errorType || null,
    entrypoint: normalizeEntrypoint(entrypoint),
  })
}

interface TrackTrialUpgradeSkippedPayload {
  location?: string
  reason?: string
  targetTier?: string
  entrypoint?: string
}
export const trackTrialUpgradeSkipped = ({ location, reason, targetTier, entrypoint }: TrackTrialUpgradeSkippedPayload) => {
  trackEvent("Trial/Upgrade Skipped", {
    location: location || null,
    reason: reason || null,
    target_tier: targetTier || null,
    entrypoint: entrypoint || null,
  })
}

// ============================================================================
// BILLING / PAYMENT METHOD UPDATE EVENTS
// ============================================================================

interface TrackPaymentMethodUpdateStartedPayload {
  paymentMethodType: PaymentMethodType
  requiresAction?: boolean
  entrypoint?: string
}

interface TrackPaymentMethodUpdateMethodSelectedPayload {
  paymentMethodType: PaymentMethodType
  requiresAction?: boolean
  isBillingUpdateFlow?: boolean
  entrypoint?: string
}
export const trackPaymentMethodUpdateMethodSelected = ({
  paymentMethodType,
  requiresAction,
  isBillingUpdateFlow,
  entrypoint,
}: TrackPaymentMethodUpdateMethodSelectedPayload) => {
  trackEvent("Payment Method Update Method Selected", {
    payment_method_type: paymentMethodType,
    requires_action: !!requiresAction,
    is_billing_update_flow: !!isBillingUpdateFlow,
    entrypoint: normalizeEntrypoint(entrypoint),
  })
}
export const trackPaymentMethodUpdateStarted = ({
  paymentMethodType,
  requiresAction,
  entrypoint,
}: TrackPaymentMethodUpdateStartedPayload) => {
  trackEvent("Payment Method Update Started", {
    payment_method_type: paymentMethodType,
    requires_action: !!requiresAction,
    entrypoint: entrypoint || null,
  })
}

interface TrackPaymentMethodUpdateCompletedPayload extends TrackPaymentMethodUpdateStartedPayload {}
export const trackPaymentMethodUpdateCompleted = ({
  paymentMethodType,
  requiresAction,
  entrypoint,
}: TrackPaymentMethodUpdateCompletedPayload) => {
  trackEvent("Payment Method Update Completed", {
    payment_method_type: paymentMethodType,
    requires_action: !!requiresAction,
    entrypoint: entrypoint || null,
  })
}

interface TrackPaymentMethodUpdateFailedPayload extends TrackPaymentMethodUpdateStartedPayload {
  errorType?: string
}
export const trackPaymentMethodUpdateFailed = ({
  paymentMethodType,
  requiresAction,
  entrypoint,
  errorType,
}: TrackPaymentMethodUpdateFailedPayload) => {
  trackEvent("Payment Method Update Failed", {
    payment_method_type: paymentMethodType,
    requires_action: !!requiresAction,
    entrypoint: entrypoint || null,
    error_type: errorType || null,
  })
}

// ============================================================================
// SEARCH EVENTS
// ============================================================================

interface TrackSearchPerformedPayload {
  term: string
  appletResultCount: number
  serviceResultCount: number
}
export const trackSearchPerformed = ({
  term,
  appletResultCount,
  serviceResultCount,
}: TrackSearchPerformedPayload) => {
  if (!term || term.trim().length === 0) return
  // Downcase the term — OpenSearch matching is case-insensitive, so tracking
  // `Weather` and `weather` as distinct values would fragment analysis.
  trackEvent("Search Performed", {
    term: term.toLowerCase(),
    applet_result_count: appletResultCount,
    service_result_count: serviceResultCount,
  })
}

interface TrackSearchResultClickedPayload {
  term: string
  resultType: "applet" | "service"
  resultId: string
  rank: number
}
export const trackSearchResultClicked = ({
  term,
  resultType,
  resultId,
  rank,
}: TrackSearchResultClickedPayload) => {
  if (!term || term.trim().length === 0) return
  trackEvent("Search Result Clicked", {
    term: term.toLowerCase(),
    result_type: resultType,
    result_id: resultId,
    rank,
  })
}

interface TrackExploreResultClickedPayload {
  resultType: string
  resultId: string
  rank: number
  isSignedIn: boolean
}
export const trackExploreResultClicked = ({
  resultType,
  resultId,
  rank,
  isSignedIn,
}: TrackExploreResultClickedPayload) => {
  trackEvent("Explore Result Clicked", {
    result_type: resultType,
    result_id: resultId,
    rank,
    is_signed_in: isSignedIn,
  })
}

interface TrackPromptHeroSubmittedPayload {
  source: string
  promptLength: number
  isSignedIn: boolean
}
// The submit navigates away at once, so it must use sendBeacon to survive navigation.
export const trackPromptHeroSubmitted = ({ source, promptLength, isSignedIn }: TrackPromptHeroSubmittedPayload) => {
  trackEvent(
    "Prompt Hero Submitted",
    {
      source,
      prompt_length: promptLength,
      is_signed_in: isSignedIn,
    },
    { transport: "sendBeacon" }
  )
}

// ============================================================================
// CONVERSATIONAL UI EVENTS (sc-143378)
// ============================================================================

interface TrackConversationalUIAppletCreatedPayload {
  conversationId: string | null
  appletId: string
  promptsToCreation: number
  conversationDurationSeconds: number
}
export const trackConversationalUIAppletCreated = ({
  conversationId,
  appletId,
  promptsToCreation,
  conversationDurationSeconds,
}: TrackConversationalUIAppletCreatedPayload) => {
  trackEvent("Conversational UI Applet Created", {
    conversation_id: conversationId,
    applet_id: appletId,
    prompts_to_creation: promptsToCreation,
    conversation_duration_seconds: conversationDurationSeconds,
  })
}

interface TrackConversationalUIAppletPostCreationEditPayload {
  conversationId: string | null
  appletId: string
}
export const trackConversationalUIAppletPostCreationEdit = ({
  conversationId,
  appletId,
}: TrackConversationalUIAppletPostCreationEditPayload) => {
  trackEvent("Conversational UI Applet Post Creation Edit", {
    conversation_id: conversationId,
    applet_id: appletId,
  })
}

export type ConversationGoalType =
  | "create_applet"
  | "edit_applet"
  | "troubleshoot_applet"
  | "explore_applets"
  | "explore_services"
  | "unclear"
export type ConversationEntryPoint =
  | "homepage_prompt"
  | "create_applet"
  | "edit_applet"
  | "explore_tab"
  | "try_it"
  | "build_automation"
export type ConversationOutcome =
  | "success_goal_achieved"
  | "abandoned_mid_flow"
  | "error_blocking"
  | "redirected_to_classic"
export type ConversationExitMethod = "natural_completion" | "navigation_away" | "chat_reset"

interface TrackConversationalUISessionStartedPayload {
  conversationId: string
  goalType: ConversationGoalType
  entryPoint: ConversationEntryPoint
  sessionStartTimestamp: string
}
export const trackConversationalUISessionStarted = ({
  conversationId,
  goalType,
  entryPoint,
  sessionStartTimestamp,
}: TrackConversationalUISessionStartedPayload) => {
  trackEvent("Conversational UI Session Started", {
    conversation_id: conversationId,
    goal_type: goalType,
    entry_point: entryPoint,
    session_start_timestamp: sessionStartTimestamp,
  })
}

interface TrackConversationalUISessionEndedPayload {
  conversationId: string
  totalUserPrompts: number
  totalAiResponses: number
  conversationOutcome: ConversationOutcome
  goalAchieved: boolean
  exitMethod: ConversationExitMethod
  sessionEndTimestamp: string
  sessionDurationSeconds: number
  viaBeacon?: boolean
}
export const trackConversationalUISessionEnded = ({
  conversationId,
  totalUserPrompts,
  totalAiResponses,
  conversationOutcome,
  goalAchieved,
  exitMethod,
  sessionEndTimestamp,
  sessionDurationSeconds,
  viaBeacon,
}: TrackConversationalUISessionEndedPayload) => {
  trackEvent(
    "Conversational UI Session Ended",
    {
      conversation_id: conversationId,
      total_user_prompts: totalUserPrompts,
      total_ai_responses: totalAiResponses,
      conversation_outcome: conversationOutcome,
      goal_achieved: goalAchieved,
      exit_method: exitMethod,
      session_end_timestamp: sessionEndTimestamp,
      session_duration_seconds: sessionDurationSeconds,
    },
    viaBeacon ? { transport: "sendBeacon" } : undefined
  )
}

// ============================================================================
// TQA "TRY IT" PREFILL EVENTS (sc-146433)
// ============================================================================

export interface TryItPrefillPayload {
  sourceService: string
  sourceServiceModule?: string | null
  sourceTqa: string
  tqaType: "trigger" | "query" | "action"
  activeTab?: string | null
}
const tryItPrefillProperties = ({
  sourceService,
  sourceServiceModule,
  sourceTqa,
  tqaType,
  activeTab,
}: TryItPrefillPayload) => ({
  source_service: sourceService,
  source_service_module: sourceServiceModule || null,
  source_tqa: sourceTqa,
  tqa_type: tqaType,
  active_tab: activeTab || null,
})

export const trackTryItPrefillShown = (payload: TryItPrefillPayload) => {
  trackEvent("TQA Try It Prefill Shown", tryItPrefillProperties(payload))
}

export const trackTryItPrefillSent = ({ promptEdited, ...base }: TryItPrefillPayload & { promptEdited: boolean }) => {
  trackEvent("TQA Try It Prefill Sent", { ...tryItPrefillProperties(base), prompt_edited: promptEdited })
}

// Fired on pagehide, so it must use sendBeacon to survive navigation.
export const trackTryItPrefillAbandoned = (payload: TryItPrefillPayload) => {
  trackEvent("TQA Try It Prefill Abandoned", tryItPrefillProperties(payload), { transport: "sendBeacon" })
}

// ============================================================================
// BUILD AUTOMATION PREFILL EVENTS (sc-148292)
// ============================================================================

export interface BuildAutomationPrefillPayload {
  sourceTrigger: string
  sourceTriggerService: string
  sourceAction: string
  sourceActionService: string
  triggerServiceModule?: string | null
  actionServiceModule?: string | null
  selectionChanged: "none" | "trigger" | "action" | "both"
}
const buildAutomationPrefillProperties = ({
  sourceTrigger,
  sourceTriggerService,
  sourceAction,
  sourceActionService,
  triggerServiceModule,
  actionServiceModule,
  selectionChanged,
}: BuildAutomationPrefillPayload) => ({
  source_trigger: sourceTrigger,
  source_trigger_service: sourceTriggerService,
  source_action: sourceAction,
  source_action_service: sourceActionService,
  trigger_service_module: triggerServiceModule || null,
  action_service_module: actionServiceModule || null,
  selection_changed: selectionChanged,
})

export const trackBuildAutomationPrefillShown = (payload: BuildAutomationPrefillPayload) => {
  trackEvent("Build Automation Prefill Shown", buildAutomationPrefillProperties(payload))
}

export const trackBuildAutomationPrefillSent = ({
  promptEdited,
  ...base
}: BuildAutomationPrefillPayload & { promptEdited: boolean }) => {
  trackEvent("Build Automation Prefill Sent", {
    ...buildAutomationPrefillProperties(base),
    prompt_edited: promptEdited,
  })
}

// Fired on pagehide, so it must use sendBeacon to survive navigation.
export const trackBuildAutomationPrefillAbandoned = (payload: BuildAutomationPrefillPayload) => {
  trackEvent("Build Automation Prefill Abandoned", buildAutomationPrefillProperties(payload), {
    transport: "sendBeacon",
  })
}

interface TrackConversationalUIStarterSelectedPayload {
  conversationId: string
  suggestion: string
}
export const trackConversationalUIStarterSelected = ({
  conversationId,
  suggestion,
}: TrackConversationalUIStarterSelectedPayload) => {
  trackEvent("Conversational UI Starter Selected", {
    conversation_id: conversationId,
    prompt_source: "starter",
    suggestion,
  })
}

export type ConversationUserAction = "sent_another_prompt" | "advanced_via_card" | "dismissed_card"

interface TrackConversationalUITurnCompletedPayload {
  conversationId: string
  turnNumber: number
  responseTime: number
  userActionAfterResponse: ConversationUserAction
  errorOccurred: boolean
  mcpToolCalled?: string
  mcpToolSuccess?: boolean
}
export const trackConversationalUITurnCompleted = ({
  conversationId,
  turnNumber,
  responseTime,
  userActionAfterResponse,
  errorOccurred,
  mcpToolCalled,
  mcpToolSuccess,
}: TrackConversationalUITurnCompletedPayload) => {
  const properties: Record<string, unknown> = {
    conversation_id: conversationId,
    turn_number: turnNumber,
    response_time: responseTime,
    user_action_after_response: userActionAfterResponse,
    error_occurred: errorOccurred,
  }
  if (mcpToolCalled !== undefined) {
    properties.mcp_tool_called = mcpToolCalled
    properties.mcp_tool_success = mcpToolSuccess ?? false
  }
  trackEvent("Conversational UI Turn Completed", properties)
}
